import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { formatDate, formatDateTime, formatCurrency } from '../utils/formatters';
import DuplicateReviewBanner from '../components/DuplicateReviewBanner';

const VALID_TABS = ['users', 'cogs', 'break-products', 'import', 'usernames', 'api', 'backups', 'history', 'activity', 'apps', 'surprise'];

function OrderStatusRefresh() {
  const [status, setStatus] = useState(null);
  const pollRef = useRef(null);

  // Check status on mount
  useEffect(() => {
    api.get('/api/dashboard/refresh-order-statuses').then(({ data }) => {
      setStatus(data);
      if (data.running) startPolling();
    }).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  function startPolling() {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get('/api/dashboard/refresh-order-statuses');
        setStatus(data);
        if (!data.running) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {}
    }, 2000);
  }

  async function handleRefresh() {
    try {
      const { data } = await api.post('/api/dashboard/refresh-order-statuses');
      setStatus(data);
      startPolling();
    } catch (err) {
      setStatus({ running: false, error: err.response?.data?.error || 'Refresh failed' });
    }
  }

  const pct = status?.total > 0 ? Math.round((status.processed / status.total) * 100) : 0;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 className="card-title">Order Status Refresh</h3>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Check TikTok for status updates on all open orders (not Completed or Cancelled). Runs automatically at 7 AM and 7 PM ET.
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleRefresh} disabled={status?.running}>
          {status?.running ? 'Running...' : 'Refresh Now'}
        </button>
      </div>
      {status?.running && status.total > 0 && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
            <span>{status.processed.toLocaleString()} / {status.total.toLocaleString()} orders checked</span>
            <span>{status.updated} updated</span>
          </div>
          <div style={{ background: 'var(--border)', borderRadius: '4px', height: '8px' }}>
            <div style={{ background: 'var(--accent)', borderRadius: '4px', height: '100%', width: `${pct}%`, transition: 'width 0.3s' }} />
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>{pct}% complete</div>
        </div>
      )}
      {!status?.running && status?.total > 0 && status?.processed > 0 && (
        <div style={{ marginTop: '10px', fontSize: '13px', color: 'var(--success)' }}>
          Last run: {status.updated} of {status.total.toLocaleString()} orders updated
        </div>
      )}
      {status?.error && (
        <div style={{ marginTop: '10px', fontSize: '13px', color: 'var(--danger)' }}>{status.error}</div>
      )}
    </div>
  );
}

// Break Products tab — controls which catalog products the break and
// freebie-COGS pickers offer. Every product is available until it's switched
// off here, which is how break-spot listings get kept out of break contents.
// Self-contained: it owns its own fetching so none of the shared admin state
// has to know about it.
function BreakProductsTab() {
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalSkus, setTotalSkus] = useState(0);
  const [excludedSkus, setExcludedSkus] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');          // the search actually applied
  const [availability, setAvailability] = useState('all');
  const [expanded, setExpanded] = useState(new Set());
  const [variations, setVariations] = useState({}); // key -> variation rows
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const groupKey = g => `${g.shop_id}|${g.product_name}`;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/break-products', {
        params: { search: query, availability, page, limit: 50 },
      });
      setGroups(data.groups || []);
      setTotal(data.total || 0);
      setTotalSkus(data.totalSkus || 0);
      setExcludedSkus(data.excludedSkus || 0);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load products');
    }
  }, [query, availability, page]);

  useEffect(() => { load(); }, [load]);

  // Re-pull any expanded group so its per-SKU rows match what just changed.
  async function refreshExpanded() {
    const keys = [...expanded];
    if (keys.length === 0) return;
    const next = {};
    await Promise.all(keys.map(async key => {
      const [shopId, productName] = key.split('|');
      try {
        const { data } = await api.get('/api/admin/products/group-variations', {
          params: { shopId, productName },
        });
        next[key] = data.variations || [];
      } catch { next[key] = variations[key] || []; }
    }));
    setVariations(v => ({ ...v, ...next }));
  }

  async function toggleExpand(g) {
    const key = groupKey(g);
    const next = new Set(expanded);
    if (next.has(key)) {
      next.delete(key);
      setExpanded(next);
      return;
    }
    next.add(key);
    setExpanded(next);
    if (!variations[key]) {
      try {
        const { data } = await api.get('/api/admin/products/group-variations', {
          params: { shopId: g.shop_id, productName: g.product_name },
        });
        setVariations(v => ({ ...v, [key]: data.variations || [] }));
      } catch { setVariations(v => ({ ...v, [key]: [] })); }
    }
  }

  // Both toggles flip their row locally before the request goes out. These are
  // controlled checkboxes: without the local flip the box snaps back to its old
  // state for the length of the round-trip, which reads as a click that didn't
  // take. The reload afterwards is authoritative either way — including on a
  // failure, where it puts the real state back under the error banner.
  async function setGroupAvailable(g, available) {
    if (busy) return;
    const key = groupKey(g);
    setGroups(gs => gs.map(row => (groupKey(row) === key
      ? { ...row, available_count: available ? row.variation_count : 0 }
      : row)));
    setVariations(prev => (prev[key]
      ? { ...prev, [key]: prev[key].map(r => ({ ...r, available_for_breaks: available ? 1 : 0 })) }
      : prev));
    setBusy(true);
    try {
      await api.put('/api/admin/break-products/group', {
        shop_id: g.shop_id, product_name: g.product_name, available,
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    }
    await load();
    await refreshExpanded();
    setBusy(false);
  }

  async function setSkuAvailable(v, available) {
    if (busy) return;
    const key = `${v.shop_id}|${v.product_name}`;
    setVariations(prev => (prev[key]
      ? { ...prev, [key]: prev[key].map(r => (r.id === v.id ? { ...r, available_for_breaks: available ? 1 : 0 } : r)) }
      : prev));
    setGroups(gs => gs.map(row => (groupKey(row) === key
      ? { ...row, available_count: Math.max(0, Math.min(row.variation_count, Number(row.available_count) + (available ? 1 : -1))) }
      : row)));
    setBusy(true);
    try {
      await api.put(`/api/admin/break-products/${v.id}`, { available });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    }
    await load();
    await refreshExpanded();
    setBusy(false);
  }

  async function setAllShown(available) {
    if (groups.length === 0) return;
    const verb = available ? 'Include' : 'Exclude';
    if (!window.confirm(`${verb} all ${groups.length} product${groups.length !== 1 ? 's' : ''} shown on this page?`)) return;
    setBusy(true);
    try {
      await api.put('/api/admin/break-products/bulk/set', {
        groups: groups.map(g => ({ shop_id: g.shop_id, product_name: g.product_name })),
        available,
      });
      await load();
      await refreshExpanded();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    }
    setBusy(false);
  }

  function applySearch() { setQuery(search); setPage(1); }

  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Break Products</h3>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          {excludedSkus} of {totalSkus} SKUs excluded
        </span>
      </div>

      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
        Choose which products can be added to a break or to freebie COGS. Everything is
        available by default — switch off the listings that are really break spots, so they
        can’t be booked as break contents. Excluded products stop appearing in the product
        pickers; COGS already recorded on a past break or freebie entry is left untouched.
      </p>

      {error && (
        <div className="badge badge-danger" style={{ display: 'block', marginBottom: '12px', padding: '8px' }}>{error}</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="Search products by name, ID, or SKU..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') applySearch(); }}
          style={{ maxWidth: '340px' }}
        />
        <button className="btn btn-secondary btn-sm" onClick={applySearch}>Search</button>
        <select value={availability} onChange={e => { setAvailability(e.target.value); setPage(1); }}
          style={{ width: 'auto', padding: '6px 8px' }}>
          <option value="all">All products</option>
          <option value="available">Available only</option>
          <option value="excluded">Excluded only</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          <button className="btn btn-secondary btn-sm" disabled={busy || groups.length === 0}
            onClick={() => setAllShown(true)}>Include all shown</button>
          <button className="btn btn-secondary btn-sm" disabled={busy || groups.length === 0}
            onClick={() => setAllShown(false)}>Exclude all shown</button>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: '90px', textAlign: 'center', whiteSpace: 'nowrap' }}>In pickers</th>
              <th style={{ textAlign: 'left' }}>Product</th>
              <th style={{ textAlign: 'left' }}>SKU</th>
              <th style={{ textAlign: 'left' }}>Category</th>
              <th style={{ textAlign: 'center' }}>Sold</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(g => {
              const key = groupKey(g);
              const isExpanded = expanded.has(key);
              const hasMultiple = g.variation_count > 1;
              const availableCount = Number(g.available_count) || 0;
              const allOn = availableCount === g.variation_count;
              const someOn = availableCount > 0 && !allOn;
              const rows = variations[key] || [];

              return (
                <React.Fragment key={key}>
                  <tr style={{ cursor: hasMultiple ? 'pointer' : 'default' }}
                    onClick={() => hasMultiple && toggleExpand(g)}>
                    <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={allOn}
                        ref={el => { if (el) el.indeterminate = someOn; }}
                        disabled={busy}
                        title={someOn ? `${availableCount} of ${g.variation_count} variations available` : undefined}
                        onChange={e => setGroupAvailable(g, e.target.checked)}
                        style={{ width: 'auto' }}
                      />
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {hasMultiple && (
                          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', transition: 'transform 0.15s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}>&#9654;</span>
                        )}
                        <div>
                          <div style={{ fontWeight: 500, color: availableCount === 0 ? 'var(--text-light)' : undefined }}>
                            {g.product_name}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-light)', fontFamily: 'monospace' }}>
                            {g.first_product_id}
                            {hasMultiple && (
                              <span style={{ marginLeft: '8px', fontFamily: 'inherit' }}>
                                · {availableCount}/{g.variation_count} variations available
                              </span>
                            )}
                          </div>
                        </div>
                        {availableCount === 0 && <span className="badge badge-warning">Excluded</span>}
                        {someOn && <span className="badge badge-default">Partial</span>}
                      </div>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{hasMultiple ? '--' : (g.first_sku || '--')}</td>
                    <td><span className="badge badge-default">{g.category || 'Uncategorized'}</span></td>
                    <td style={{ textAlign: 'center' }}>{g.total_sold || 0}</td>
                  </tr>

                  {isExpanded && rows.map(v => (
                    <tr key={v.id} style={{ background: 'var(--bg)' }}>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!v.available_for_breaks}
                          disabled={busy}
                          onChange={e => setSkuAvailable(v, e.target.checked)}
                          style={{ width: 'auto' }}
                        />
                      </td>
                      <td style={{ paddingLeft: '36px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontWeight: 500, fontSize: '13px', color: v.available_for_breaks ? undefined : 'var(--text-light)' }}>
                            {v.tiktok_sku_name || v.sku || 'Variation'}
                          </span>
                          {!v.available_for_breaks && <span className="badge badge-warning">Excluded</span>}
                        </div>
                        <div style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-light)' }}>{v.product_id}</div>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{v.sku || '--'}</td>
                      <td><span className="badge badge-default">{v.category || 'Uncategorized'}</span></td>
                      <td style={{ textAlign: 'center', fontSize: '12px' }}>{v.total_sold || 0}</td>
                    </tr>
                  ))}
                  {isExpanded && rows.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: '12px', padding: '8px' }}>No variations</td></tr>
                  )}
                </React.Fragment>
              );
            })}
            {groups.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-light)', padding: '24px' }}>No products found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {total > 50 && (
        <div className="pagination">
          <span className="pagination-info">{total} products ({totalSkus} SKUs total) · page {page} of {totalPages}</span>
          <div className="pagination-btns">
            <button className="btn btn-secondary btn-sm" disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}>Prev</button>
            <button className="btn btn-secondary btn-sm" disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { tab: urlTab } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTabState] = useState(() => VALID_TABS.includes(urlTab) ? urlTab : 'users');

  function setTab(t) {
    setTabState(t);
    navigate(`/admin/${t}`, { replace: true });
  }
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [apiHealth, setApiHealth] = useState(null);
  const [backups, setBackups] = useState([]);
  const [activityLog, setActivityLog] = useState({ logs: [], total: 0, page: 1 });
  const [history, setHistory] = useState({ items: [], total: 0, page: 1 });
  const [historyBusy, setHistoryBusy] = useState(null); // id being reverted
  const [historyConfirm, setHistoryConfirm] = useState(null); // entry being confirmed
  const [backfill, setBackfill] = useState([]);
  const [externalApps, setExternalApps] = useState([]);
  const [surpriseCols, setSurpriseCols] = useState([]);
  const [surpriseDirty, setSurpriseDirty] = useState(false);
  const [surpriseSaving, setSurpriseSaving] = useState(false);
  const [surpriseError, setSurpriseError] = useState(null);
  const [surpriseSaved, setSurpriseSaved] = useState(false);
  const [surprisePreviewLines, setSurprisePreviewLines] = useState([]);
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', role: 'view_only' });
  const [showUserForm, setShowUserForm] = useState(false);
  const [productGroups, setProductGroups] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [productTotalSkus, setProductTotalSkus] = useState(0);
  const [productMissingCogs, setProductMissingCogs] = useState(0);
  const [filterMissingCogs, setFilterMissingCogs] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customForm, setCustomForm] = useState({ name: '', price: '', cogs: '', sku: '' });
  const [editingGroup, setEditingGroup] = useState(null);
  const [editGroupCogs, setEditGroupCogs] = useState('');
  const [editingVariation, setEditingVariation] = useState(null);
  const [editVariationCogs, setEditVariationCogs] = useState('');
  const [productSortCol, setProductSortCol] = useState('product_name');
  const [productSortDir, setProductSortDir] = useState('asc');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [groupVariations, setGroupVariations] = useState({});
  const [resetPwUser, setResetPwUser] = useState(null);
  const [resetPwValue, setResetPwValue] = useState('');
  const [resetPwMsg, setResetPwMsg] = useState(null);
  const [userMsg, setUserMsg] = useState(null);
  const [editNameUser, setEditNameUser] = useState(null);
  const [editNameValue, setEditNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [importShops, setImportShops] = useState([]);
  const [importShopId, setImportShopId] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importCount, setImportCount] = useState(0);
  const [metricsFile, setMetricsFile] = useState(null);
  const [metricsResult, setMetricsResult] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsCount, setMetricsCount] = useState(0);

  // Username matcher tab
  const [usernameShopFilter, setUsernameShopFilter] = useState('');
  const [usernameResult, setUsernameResult] = useState(null);
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [usernameError, setUsernameError] = useState(null);
  const [usernameRanMode, setUsernameRanMode] = useState(null); // 'dry' | 'wet' | null
  const [usernameSortKey, setUsernameSortKey] = useState('lifetime_value');
  const [usernameSortDir, setUsernameSortDir] = useState('desc'); // 'asc' | 'desc'

  async function runUsernameMatcher(dryRun) {
    setUsernameLoading(true);
    setUsernameError(null);
    try {
      const { data } = await api.post('/api/admin/buyers/match-usernames', {
        shopId: usernameShopFilter || undefined,
        dryRun,
      });
      setUsernameResult(data);
      setUsernameRanMode(dryRun ? 'dry' : 'wet');
    } catch (err) {
      setUsernameError(err.response?.data?.error || 'Failed to run matcher');
    }
    setUsernameLoading(false);
  }

  useEffect(() => { fetchTabData(); }, [tab]);

  // When deep-linked to /admin/cogs?search=PRODUCT_ID (e.g., from the
  // "new product needs COGS" notification), prefill the search box and
  // run the query so the operator lands on a filtered list.
  useEffect(() => {
    if (tab !== 'cogs') return;
    const search = new URLSearchParams(location.search).get('search');
    if (search) {
      setProductSearch(search);
      setProductPage(1);
      fetchProducts(search, 1);
    }
  }, [tab, location.search]);

  const fetchProducts = useCallback(async (search, page, sortCol, sortDir, missingCogsOverride) => {
    try {
      const sc = sortCol || productSortCol;
      const sd = sortDir || productSortDir;
      const mc = (missingCogsOverride !== undefined ? missingCogsOverride : filterMissingCogs) ? '&missingCogs=1' : '';
      const { data } = await api.get(`/api/admin/products/grouped?search=${encodeURIComponent(search || '')}&page=${page}&limit=50&sortBy=${sc}&sortDir=${sd}${mc}`);
      setProductGroups(data.groups);
      setProductTotal(data.total);
      setProductTotalSkus(data.totalSkus);
      setProductMissingCogs(data.missingCogs);
    } catch (err) { console.error('Products fetch error:', err); }
  }, [productSortCol, productSortDir, filterMissingCogs]);

  async function fetchTabData() {
    try {
      switch (tab) {
        case 'users': {
          const [u, p] = await Promise.all([api.get('/api/admin/users'), api.get('/api/admin/permissions')]);
          setUsers(u.data.users); setPermissions(p.data.permissions); break;
        }
        case 'api': {
          const [h, bf] = await Promise.all([api.get('/api/admin/api-health'), api.get('/api/admin/backfill-progress')]);
          setApiHealth(h.data); setBackfill(bf.data.progress); break;
        }
        case 'backups': { const b = await api.get('/api/admin/backups'); setBackups(b.data.backups); break; }
        case 'activity': { const a = await api.get(`/api/admin/activity-log?page=${activityLog.page}`); setActivityLog(a.data); break; }
        case 'history': { const h = await api.get(`/api/admin/history?page=${history.page}`); setHistory(h.data); break; }
        case 'apps': { const a = await api.get('/api/admin/external-apps'); setExternalApps(a.data.apps); break; }
        case 'surprise': {
          const [s, tm] = await Promise.all([
            api.get('/api/admin/surprise-set-settings'),
            api.get('/api/admin/surprise-set-team-markets', { params: { category: 'Multi-Sport', breakNumber: 8819 } }),
          ]);
          setSurpriseCols(s.data.columns || []);
          setSurprisePreviewLines(tm.data.lines || []);
          setSurpriseDirty(false); setSurpriseError(null);
          break;
        }
        case 'cogs': { fetchProducts(productSearch, productPage); break; }
        case 'import': {
          const [s, c, mc] = await Promise.all([api.get('/api/admin/shops'), api.get('/api/admin/import-orders/count'), api.get('/api/admin/import-metrics/count')]);
          setImportShops(s.data.shops.filter(sh => sh.is_active));
          setImportCount(c.data.count);
          setMetricsCount(mc.data.count);
          if (!importShopId && s.data.shops.length > 0) setImportShopId(s.data.shops[0].shop_id);
          break;
        }
        case 'usernames': {
          // Reuse the shops list for the optional scope filter.
          const s = await api.get('/api/admin/shops');
          setImportShops(s.data.shops.filter(sh => sh.is_active));
          break;
        }
        default: break;
      }
    } catch (err) { console.error('Admin fetch error:', err); }
  }

  // Every user-management call reports through this banner. Without it a failed
  // request just rejects into the void and the row looks like nothing happened.
  function reportUserError(err, fallback) {
    console.error(fallback, err);
    setUserMsg({ type: 'error', text: err.response?.data?.error || fallback });
  }

  async function createUser(e) {
    e.preventDefault();
    try {
      setUserMsg(null);
      await api.post('/api/admin/users', newUser);
      setShowUserForm(false);
      setNewUser({ email: '', name: '', password: '', role: 'view_only' });
      fetchTabData();
    } catch (err) { reportUserError(err, 'Failed to create user'); }
  }

  async function updateRole(userId, role) {
    try {
      setUserMsg(null);
      await api.put(`/api/admin/users/${userId}`, { role });
      fetchTabData();
    } catch (err) { reportUserError(err, 'Failed to update role'); }
  }

  function startEditName(user) {
    setEditNameUser(user.id);
    setEditNameValue(user.name);
    setUserMsg(null);
  }

  function cancelEditName() {
    setEditNameUser(null);
    setEditNameValue('');
  }

  async function saveName(userId) {
    const name = editNameValue.trim();
    if (!name) { setUserMsg({ type: 'error', text: 'Name cannot be empty' }); return; }
    try {
      setSavingName(true);
      setUserMsg(null);
      await api.put(`/api/admin/users/${userId}`, { name });
      cancelEditName();
      setUserMsg({ type: 'success', text: 'Name updated' });
      fetchTabData();
    } catch (err) {
      reportUserError(err, 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  }

  async function resetPassword(userId) {
    if (resetPwValue.length < 6) { setResetPwMsg('Password must be at least 6 characters'); return; }
    try {
      await api.put(`/api/admin/users/${userId}/reset-password`, { password: resetPwValue });
      setResetPwUser(null); setResetPwValue(''); setResetPwMsg(null);
    } catch (err) { setResetPwMsg(err.response?.data?.error || 'Failed to reset password'); }
  }

  async function setUserActive(user, active) {
    if (!active && !window.confirm(
      `Deactivate ${user.name} (${user.email})?\n\n` +
      'They will be signed out and blocked from logging in. All of their data ' +
      'is kept — breaks, COGS, tasks, comments, inventory history and the ' +
      'activity log stay exactly as they are. You can reactivate them anytime.'
    )) return;
    try {
      setUserMsg(null);
      await api.put(`/api/admin/users/${user.id}/active`, { active });
      setUserMsg({ type: 'success', text: `${active ? 'Reactivated' : 'Deactivated'} ${user.name}` });
      fetchTabData();
    } catch (err) {
      reportUserError(err, `Failed to ${active ? 'reactivate' : 'deactivate'} user`);
    }
  }

  async function togglePermission(role, permission, enabled) {
    await api.put('/api/admin/permissions', { role, permission, enabled });
    fetchTabData();
  }

  async function runBackup() {
    await api.post('/api/admin/backup-now');
    fetchTabData();
  }

  async function updateExternalApp(id, updates) {
    await api.put(`/api/admin/external-apps/${id}`, updates);
    fetchTabData();
  }

  function markSurpriseDirty(nextCols) {
    setSurpriseCols(nextCols);
    setSurpriseDirty(true);
    setSurpriseError(null);
    setSurpriseSaved(false);
  }
  function updateCol(i, patch) {
    markSurpriseDirty(surpriseCols.map((c, idx) => {
      if (idx !== i) return c;
      const next = { ...c, ...patch };
      if (patch.fill) next.fill = { ...c.fill, ...patch.fill };
      return next;
    }));
  }
  function addCol() {
    const id = (window.crypto?.randomUUID?.() || `col_${Date.now()}`);
    markSurpriseDirty([...surpriseCols, {
      id, header: '', enabled: true, required: false, width: 20,
      instruction: '', example: '',
      fill: { mode: 'blank', value: '', valueType: 'auto', numFmt: '' },
    }]);
  }
  function deleteCol(i) {
    if (!window.confirm(`Delete the "${surpriseCols[i].header || 'untitled'}" column?`)) return;
    markSurpriseDirty(surpriseCols.filter((_, idx) => idx !== i));
  }
  function moveCol(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= surpriseCols.length) return;
    const next = [...surpriseCols];
    [next[i], next[j]] = [next[j], next[i]];
    markSurpriseDirty(next);
  }
  async function saveSurpriseCols() {
    setSurpriseSaving(true);
    setSurpriseError(null);
    try {
      await api.put('/api/admin/surprise-set-settings', { columns: surpriseCols });
      setSurpriseDirty(false);
      setSurpriseSaved(true);
      setTimeout(() => setSurpriseSaved(false), 3000);
    } catch (err) {
      setSurpriseError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSurpriseSaving(false);
    }
  }
  async function downloadSurpriseSample() {
    try {
      const { data, headers } = await api.get('/api/admin/surprise-set-sample', { responseType: 'blob' });
      const blob = new Blob([data], { type: headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'Surprise Set Batch - SAMPLE.xlsx';
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download sample');
    }
  }

  async function uploadImport(e) {
    e.preventDefault();
    if (!importFile || !importShopId) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('shop_id', importShopId);
      const { data } = await api.post('/api/admin/import-orders', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(data);
      // Refresh count
      const c = await api.get('/api/admin/import-orders/count');
      setImportCount(c.data.count);
    } catch (err) {
      setImportResult({ error: err.response?.data?.error || 'Import failed' });
    }
    setImportLoading(false);
  }

  async function uploadMetrics(e) {
    e.preventDefault();
    if (!metricsFile || !importShopId) return;
    setMetricsLoading(true);
    setMetricsResult(null);
    try {
      const formData = new FormData();
      formData.append('file', metricsFile);
      formData.append('shop_id', importShopId);
      const { data } = await api.post('/api/admin/import-metrics', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMetricsResult(data);
      const mc = await api.get('/api/admin/import-metrics/count');
      setMetricsCount(mc.data.count);
    } catch (err) {
      setMetricsResult({ error: err.response?.data?.error || 'Import failed' });
    }
    setMetricsLoading(false);
  }

  async function clearImportedData() {
    if (!window.confirm('Delete ALL CSV-imported data (orders, ad spend, auction COGS)? This cannot be undone.')) return;
    try {
      const { data } = await api.delete('/api/admin/import-orders');
      setImportResult({ cleared: true, ...data });
      setImportCount(0);
      setMetricsCount(0);
      setMetricsResult(null);
    } catch (err) {
      setImportResult({ error: err.response?.data?.error || 'Clear failed' });
    }
  }

  async function toggleGroupExpand(g) {
    const key = `${g.shop_id}|${g.product_name}`;
    const next = new Set(expandedGroups);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
      if (!groupVariations[key]) {
        try {
          const { data } = await api.get(`/api/admin/products/group-variations?shopId=${encodeURIComponent(g.shop_id)}&productName=${encodeURIComponent(g.product_name)}`);
          setGroupVariations(prev => ({ ...prev, [key]: data.variations }));
        } catch (err) { console.error('Fetch variations error:', err); }
      }
    }
    setExpandedGroups(next);
  }

  async function saveGroupCogs(g) {
    try {
      await api.put('/api/admin/products/group-cogs', {
        shop_id: g.shop_id, product_name: g.product_name,
        cogs: editGroupCogs === '' ? null : parseFloat(editGroupCogs)
      });
      setEditingGroup(null);
      setEditGroupCogs('');
      // Refresh variations cache
      const key = `${g.shop_id}|${g.product_name}`;
      if (groupVariations[key]) {
        const { data } = await api.get(`/api/admin/products/group-variations?shopId=${encodeURIComponent(g.shop_id)}&productName=${encodeURIComponent(g.product_name)}`);
        setGroupVariations(prev => ({ ...prev, [key]: data.variations }));
      }
      fetchProducts(productSearch, productPage);
    } catch (err) { console.error('Save group COGS error:', err); }
  }

  async function saveVariationCogs(v) {
    try {
      await api.put(`/api/admin/products/${v.id}/cogs`, {
        cogs: editVariationCogs === '' ? null : parseFloat(editVariationCogs)
      });
      setEditingVariation(null);
      setEditVariationCogs('');
      // Refresh the variation in cache
      const key = `${v.shop_id}|${v.product_name}`;
      if (groupVariations[key]) {
        const { data } = await api.get(`/api/admin/products/group-variations?shopId=${encodeURIComponent(v.shop_id)}&productName=${encodeURIComponent(v.product_name)}`);
        setGroupVariations(prev => ({ ...prev, [key]: data.variations }));
      }
      fetchProducts(productSearch, productPage);
    } catch (err) { console.error('Save variation COGS error:', err); }
  }

  async function createCustomProduct(e) {
    e.preventDefault();
    if (!customForm.name.trim()) return;
    try {
      await api.post('/api/admin/products', {
        product_name: customForm.name.trim(),
        price: customForm.price || 0,
        cogs: customForm.cogs || null,
        sku: customForm.sku || null,
      });
      setCustomForm({ name: '', price: '', cogs: '', sku: '' });
      setShowCustomForm(false);
      fetchProducts(productSearch, productPage);
    } catch (err) {
      console.error('Create custom product error:', err);
      alert(err.response?.data?.error || 'Failed to create product');
    }
  }

  async function deleteCustomProduct(productId) {
    if (!window.confirm('Delete this custom product?')) return;
    try {
      await api.delete(`/api/admin/products/${productId}`);
      fetchProducts(productSearch, productPage);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete product');
    }
  }

  function toggleProductSort(col) {
    const newDir = productSortCol === col && productSortDir === 'asc' ? 'desc' : 'asc';
    setProductSortCol(col);
    setProductSortDir(newDir);
    setProductPage(1);
    fetchProducts(productSearch, 1, col, newDir);
  }

  const [activePermInfo, setActivePermInfo] = useState(null);
  const permInfoRef = useRef(null);

  useEffect(() => {
    if (!activePermInfo) return;
    function handleClick(e) {
      if (permInfoRef.current && !permInfoRef.current.contains(e.target)) setActivePermInfo(null);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('touchstart', handleClick); };
  }, [activePermInfo]);

  const allPermissions = permissions.admin ? Object.keys(permissions.admin) : [];
  const allRoles = ['admin', 'partner', 'operations', 'breaker', 'buyer', 'view_only'];
  const permissionInfo = {
    view_dashboard: 'See the Financials page (KPIs, revenue chart, profit chart, daily metrics table) in the sidebar and at /financials.',
    view_financials: 'See the Costs, Profitability, and Ads Performance sections inside the Financials page (otherwise they are hidden).',
    edit_cogs: 'Edit the COGS column on the Admin → COGS tab and enter actual COGS on a break via the Break Detail panel.',
    manage_breaks: 'Create new breaks, edit a break’s name/date/time/host/category/status, add or remove products, save spot prices and customers, execute spot trades, and delete breaks. Also covers break templates.',
    manage_tasks: 'Create new tasks on the Tasks page, assign them to users, and edit tasks created by others.',
    manage_users: 'Add new users on the Admin → Users tab, change another user’s role, reset their password, and delete users.',
    view_admin: 'See the Admin link in the sidebar and access the Admin Dashboard at /admin.',
    configure_notifications: 'Access the Notifications page at /notifications-config and create or edit notification rules and email alerts.',
    export_data: 'Use the Export CSV buttons on Financials, Orders, Affiliates, Payouts, and other report pages.',
    post_comments: 'Write new comments and @-mentions in any discussion thread (breaks, tasks, bulletins).',
    delete_any_comment: 'Delete comments written by other users. Without this, users can still delete their own comments.',
    view_orders: 'See the Orders link in the sidebar and access the Orders page and individual order detail views.',
    view_affiliates: 'See the Affiliates link in the sidebar and access /affiliates.',
    view_payouts: 'See the Payouts link in the sidebar and access /payouts.',
    view_customer_data: 'See the Customer Data report at /reports/customer-data, which contains the Top Customers and Top Break Customers tables.',
    post_bulletins: 'Post and edit bulletins on the Home page (the company-wide announcement feed).',
    use_ai_chat: 'Open and send messages in the Nucleus AI chat assistant.',
    manage_inventory: 'Adjust stock counts, add new inventory items, and log purchases/usage on the Inventory page.',
    view_auction_grid: 'Open the Team Auction Grid overlay (the streaming-overlay view for live breaks).',
    manage_shipping: 'See Shipping in the sidebar and use /shipping: upload the TikTok label + packing slip PDF, split it into Hitters and Skunks, and download past splits (these PDFs contain customer addresses).',
    post_break_note: 'Post a short note to a break that pops over the Break Spots page for everyone viewing it (the “Post Note” button on a break).',
  };

  return (
    <div>
      <h2 className="page-title" style={{ marginBottom: '20px' }}>Admin Dashboard</h2>

      <div className="tabs">
        {VALID_TABS.map(t => (
          <span key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t === 'api' ? 'API Health' : t === 'apps' ? 'Integrations' : t === 'cogs' ? 'COGS' : t === 'break-products' ? 'Break Products' : t === 'import' ? 'Import' : t === 'usernames' ? 'Usernames' : t === 'surprise' ? 'Surprise Set' : t.charAt(0).toUpperCase() + t.slice(1)}
          </span>
        ))}
      </div>

      {/* Users tab */}
      {tab === 'users' && (
        <div>
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Users</h3>
              <button className="btn btn-primary" onClick={() => setShowUserForm(true)}>Add User</button>
            </div>
            {userMsg && (
              <div style={{
                marginBottom: '12px', padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                color: userMsg.type === 'error' ? 'var(--danger)' : 'var(--success)',
                background: 'var(--bg)',
                border: `1px solid ${userMsg.type === 'error' ? 'var(--danger)' : 'var(--success)'}`,
              }}>
                {userMsg.text}
              </div>
            )}
            {showUserForm && (
              <form onSubmit={createUser} style={{ marginBottom: '16px', padding: '16px', background: 'var(--bg)', borderRadius: '8px' }}>
                <div className="form-row">
                  <div className="form-group"><label>Email</label><input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} required /></div>
                  <div className="form-group"><label>Name</label><input value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} required /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Password</label><input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} placeholder="Leave blank for Google-only" /></div>
                  <div className="form-group"><label>Role</label>
                    <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                      <option value="admin">Admin</option><option value="partner">Partner</option>
                      <option value="operations">Operations</option><option value="breaker">Breaker</option>
                      <option value="buyer">Buyer</option><option value="view_only">View Only</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" className="btn btn-primary">Create</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowUserForm(false)}>Cancel</button>
                </div>
              </form>
            )}
            <div className="table-container">
              <table>
                <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
                <tbody>
                  {users.map(u => (
                    <React.Fragment key={u.id}>
                    <tr style={u.is_active ? undefined : { opacity: 0.55 }}>
                      <td>
                        {editNameUser === u.id ? (
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input value={editNameValue} autoFocus
                              onChange={e => setEditNameValue(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') saveName(u.id);
                                if (e.key === 'Escape') cancelEditName();
                              }}
                              style={{ maxWidth: '160px' }} />
                            <button className="btn btn-primary btn-sm" disabled={savingName} onClick={() => saveName(u.id)}>Save</button>
                            <button className="btn btn-secondary btn-sm" disabled={savingName} onClick={cancelEditName}>Cancel</button>
                          </div>
                        ) : (
                          <span onClick={() => startEditName(u)} title="Click to rename"
                            style={{ cursor: 'pointer', borderBottom: '1px dashed var(--border)' }}>
                            {u.name}
                          </span>
                        )}
                      </td>
                      <td>{u.email}</td>
                      <td>
                        <select value={u.role} onChange={e => updateRole(u.id, e.target.value)} style={{ width: 'auto' }}>
                          <option value="admin">Admin</option><option value="partner">Partner</option>
                          <option value="operations">Operations</option><option value="breaker">Breaker</option>
                          <option value="buyer">Buyer</option><option value="view_only">View Only</option>
                        </select>
                      </td>
                      <td>
                        {u.is_active ? (
                          <span style={{ color: 'var(--success)', fontSize: '12px', fontWeight: 600 }}>Active</span>
                        ) : (
                          <span style={{ color: 'var(--text-light)', fontSize: '12px', fontWeight: 600 }}
                            title={u.deactivated_at ? `Deactivated ${formatDate(u.deactivated_at)}` : 'Deactivated'}>
                            Deactivated
                          </span>
                        )}
                      </td>
                      <td>{formatDate(u.created_at)}</td>
                      <td><div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => (editNameUser === u.id ? cancelEditName() : startEditName(u))}>
                          {editNameUser === u.id ? 'Cancel' : 'Rename'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setResetPwUser(resetPwUser === u.id ? null : u.id); setResetPwValue(''); setResetPwMsg(null); }}>
                          {resetPwUser === u.id ? 'Cancel' : 'Reset PW'}
                        </button>
                        {u.is_active ? (
                          <button className="btn btn-danger btn-sm" onClick={() => setUserActive(u, false)}>Deactivate</button>
                        ) : (
                          <button className="btn btn-primary btn-sm" onClick={() => setUserActive(u, true)}>Reactivate</button>
                        )}
                      </div></td>
                    </tr>
                    {resetPwUser === u.id && (
                      <tr>
                        <td colSpan={6}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '4px 0', flexWrap: 'wrap' }}>
                            <input type="password" placeholder="New password (min 6 chars)" value={resetPwValue}
                              onChange={e => setResetPwValue(e.target.value)} style={{ maxWidth: '250px' }} />
                            <button className="btn btn-primary btn-sm" onClick={() => resetPassword(u.id)}>Set Password</button>
                            {resetPwMsg && <span style={{ color: 'var(--danger)', fontSize: '12px' }}>{resetPwMsg}</span>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Permissions Matrix */}
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '16px' }}>Permissions Matrix</h3>
            <div className="permissions-matrix">
              <table>
                <thead>
                  <tr><th>Permission</th>{allRoles.map(r => <th key={r}>{r}</th>)}</tr>
                </thead>
                <tbody>
                  {allPermissions.map(perm => (
                    <tr key={perm}>
                      <td style={{ whiteSpace: 'nowrap', position: 'relative' }}>
                        {perm.replace(/_/g, ' ')}
                        {permissionInfo[perm] && (
                          <span ref={activePermInfo === perm ? permInfoRef : null}
                            onClick={() => setActivePermInfo(activePermInfo === perm ? null : perm)}
                            style={{ marginLeft: '4px', cursor: 'pointer', verticalAlign: 'middle', display: 'inline-block', position: 'relative' }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-light)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                              <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                            </svg>
                            {activePermInfo === perm && (
                              <div style={{
                                position: 'absolute', bottom: '100%', left: '0',
                                background: '#1a1a2e', color: 'white', padding: '8px 12px', borderRadius: '6px',
                                fontSize: '12px', lineHeight: 1.4, width: 'max-content', maxWidth: '320px',
                                whiteSpace: 'normal', textAlign: 'left', zIndex: 100, marginBottom: '4px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                              }}>
                                {permissionInfo[perm]}
                              </div>
                            )}
                          </span>
                        )}
                      </td>
                      {allRoles.map(role => (
                        <td key={role}>
                          <input type="checkbox" checked={permissions[role]?.[perm] || false}
                                 onChange={e => togglePermission(role, perm, e.target.checked)}
                                 style={{ width: 'auto' }} disabled={role === 'admin'} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* COGS tab */}
      {tab === 'cogs' && (
        <div>
          <DuplicateReviewBanner onMergeApplied={() => fetchProducts(productSearch, productPage)} />
          {productMissingCogs > 0 && (
            <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #f59e0b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>&#9888;</span>
              <span style={{ fontSize: '13px', color: '#92400e' }}>
                <strong>{productMissingCogs}</strong> product{productMissingCogs !== 1 ? 's' : ''} missing COGS. Profit calculations will be incomplete until all products have COGS set.
              </span>
            </div>
          )}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Product COGS</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className={filterMissingCogs ? 'btn btn-primary' : 'btn btn-secondary'}
                  onClick={() => {
                    const next = !filterMissingCogs;
                    setFilterMissingCogs(next);
                    setProductPage(1);
                    fetchProducts(productSearch, 1, null, null, next);
                  }}
                >
                  {filterMissingCogs ? 'Showing missing COGS only' : 'Filter for Missing COGS'}
                </button>
                <button className="btn btn-secondary" onClick={() => setShowCustomForm(!showCustomForm)}>
                  {showCustomForm ? 'Cancel' : 'Create Custom Product'}
                </button>
              </div>
            </div>
            {showCustomForm && (
              <form onSubmit={createCustomProduct} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Name</label>
                  <input value={customForm.name} onChange={e => setCustomForm({ ...customForm, name: e.target.value })} required placeholder="Product name" style={{ width: '200px' }} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Price</label>
                  <input type="number" step="0.01" min="0" value={customForm.price} onChange={e => setCustomForm({ ...customForm, price: e.target.value })} placeholder="0.00" style={{ width: '100px' }} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>COGS</label>
                  <input type="number" step="0.01" min="0" value={customForm.cogs} onChange={e => setCustomForm({ ...customForm, cogs: e.target.value })} placeholder="0.00" style={{ width: '100px' }} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>SKU</label>
                  <input value={customForm.sku} onChange={e => setCustomForm({ ...customForm, sku: e.target.value })} placeholder="Optional" style={{ width: '120px' }} />
                </div>
                <button type="submit" className="btn btn-primary btn-sm">Create</button>
              </form>
            )}
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Set COGS per product. When orders come in, COGS is auto-calculated from the product(s) in each order.
            </p>
            <div style={{ marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Search products by name, ID, or SKU..."
                value={productSearch}
                onChange={e => { setProductSearch(e.target.value); setProductPage(1); }}
                onKeyDown={e => { if (e.key === 'Enter') fetchProducts(productSearch, 1); }}
                style={{ maxWidth: '400px' }}
              />
              <button className="btn btn-secondary btn-sm" style={{ marginLeft: '8px' }}
                      onClick={() => fetchProducts(productSearch, 1)}>Search</button>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    {[
                      { key: 'product_name', label: 'Product', align: 'left' },
                      { key: 'sku', label: 'SKU', align: 'left' },
                      { key: 'category', label: 'Category', align: 'left' },
                      { key: 'price', label: 'Price', align: 'right' },
                      { key: 'cogs', label: 'COGS', align: 'right' },
                      { key: 'margin', label: 'Margin', align: 'right' },
                      { key: 'total_sold', label: 'Sold', align: 'center' },
                    ].map(col => (
                      <th key={col.label} style={{ textAlign: col.align, cursor: col.key ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}
                        onClick={() => col.key && toggleProductSort(col.key)}>
                        {col.label} {col.key && productSortCol === col.key ? (productSortDir === 'asc' ? '↑' : '↓') : ''}
                      </th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {productGroups.map(g => {
                    const key = `${g.shop_id}|${g.product_name}`;
                    const isExpanded = expandedGroups.has(key);
                    const hasMultiple = g.variation_count > 1;
                    const cogsNum = g.group_cogs != null ? parseFloat(g.group_cogs) : NaN;
                    const margin = g.avg_price > 0 && !isNaN(cogsNum) ? ((g.avg_price - cogsNum) / g.avg_price * 100) : null;
                    const isEditingG = editingGroup === key;

                    return (
                      <React.Fragment key={key}>
                        {/* Group row */}
                        <tr style={{ cursor: hasMultiple ? 'pointer' : 'default' }}
                          onClick={() => hasMultiple && toggleGroupExpand(g)}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {hasMultiple && (
                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', transition: 'transform 0.15s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}>&#9654;</span>
                              )}
                              <div>
                                <div style={{ fontWeight: 500 }}>{g.product_name}</div>
                                <div style={{ fontSize: '11px', color: 'var(--text-light)', fontFamily: 'monospace' }}>
                                  {g.first_product_id}
                                  {hasMultiple && <span style={{ marginLeft: '8px', fontFamily: 'inherit' }}>· {g.variation_count} variations</span>}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{hasMultiple ? '--' : (g.sku || '--')}</td>
                          <td><span className="badge badge-default">{g.category || 'Uncategorized'}</span></td>
                          <td style={{ textAlign: 'right' }}>
                            {hasMultiple && g.min_price !== g.max_price
                              ? <span>{formatCurrency(g.min_price)} – {formatCurrency(g.max_price)}</span>
                              : formatCurrency(g.avg_price)}
                          </td>
                          <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                            {isEditingG ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                                <input type="text" inputMode="decimal" value={editGroupCogs}
                                  onChange={e => setEditGroupCogs(e.target.value.replace(/[^0-9.]/g, ''))}
                                  onKeyDown={e => { if (e.key === 'Enter') saveGroupCogs(g); if (e.key === 'Escape') setEditingGroup(null); }}
                                  placeholder="--" autoFocus
                                  style={{ width: '80px', textAlign: 'right', padding: '4px 8px', border: '2px solid var(--accent)', background: '#ebf8ff' }} />
                                <button className="btn btn-primary btn-sm" onClick={() => saveGroupCogs(g)} style={{ padding: '2px 8px' }}>Save</button>
                                <button className="btn btn-secondary btn-sm" onClick={() => setEditingGroup(null)} style={{ padding: '2px 6px' }}>X</button>
                              </div>
                            ) : (
                              g.group_cogs != null ? formatCurrency(g.group_cogs)
                              : (g.min_cogs != null && g.max_cogs != null && !g.cogs_uniform)
                                ? <span>{formatCurrency(g.min_cogs)} – {formatCurrency(g.max_cogs)}</span>
                                : <span style={{ color: 'var(--text-light)' }}>--</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {margin !== null
                              ? <span style={{ color: margin > 30 ? 'var(--success)' : margin > 15 ? 'var(--warning)' : 'var(--danger)' }}>{margin.toFixed(1)}%</span>
                              : <span style={{ color: 'var(--text-light)' }}>--</span>}
                          </td>
                          <td style={{ textAlign: 'center' }}>{g.total_sold || 0}</td>
                          <td onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              <button className="btn btn-secondary btn-sm"
                                onClick={() => { setEditingGroup(key); setEditGroupCogs(g.group_cogs != null ? String(g.group_cogs) : ''); }}
                                title={hasMultiple ? 'Set COGS for all variations' : 'Edit COGS'}
                                style={{ padding: '4px 8px', fontSize: '14px', lineHeight: 1 }}>&#9998;</button>
                              {g.category === 'Custom' && !hasMultiple && (
                                <button className="btn btn-secondary btn-sm"
                                  onClick={() => deleteCustomProduct(g.first_id)}
                                  title="Delete custom product"
                                  style={{ padding: '4px 8px', fontSize: '14px', lineHeight: 1, color: 'var(--danger)' }}>&times;</button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {/* Expanded variation rows */}
                        {isExpanded && hasMultiple && (groupVariations[key] || []).map(v => {
                          const vCogsNum = v.cogs != null ? parseFloat(v.cogs) : NaN;
                          const vMargin = v.price > 0 && !isNaN(vCogsNum) ? ((v.price - vCogsNum) / v.price * 100) : null;
                          const isEditingV = editingVariation === v.id;
                          return (
                            <tr key={v.id} style={{ background: 'var(--bg)' }}>
                              <td style={{ paddingLeft: '36px' }}>
                                <div style={{ fontWeight: 500, fontSize: '13px' }}>{v.sku || 'Variation'}</div>
                                <div style={{ fontSize: '11px', color: 'var(--text-light)', fontFamily: 'monospace' }}>{v.product_id}</div>
                              </td>
                              <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{v.sku || '--'}</td>
                              <td></td>
                              <td style={{ textAlign: 'right' }}>{formatCurrency(v.price)}</td>
                              <td style={{ textAlign: 'right' }}>
                                {isEditingV ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                                    <input type="text" inputMode="decimal" value={editVariationCogs}
                                      onChange={e => setEditVariationCogs(e.target.value.replace(/[^0-9.]/g, ''))}
                                      onKeyDown={e => { if (e.key === 'Enter') saveVariationCogs(v); if (e.key === 'Escape') setEditingVariation(null); }}
                                      placeholder="--" autoFocus
                                      style={{ width: '80px', textAlign: 'right', padding: '4px 8px', border: '2px solid var(--accent)', background: '#ebf8ff' }} />
                                    <button className="btn btn-primary btn-sm" onClick={() => saveVariationCogs(v)} style={{ padding: '2px 8px' }}>Save</button>
                                    <button className="btn btn-secondary btn-sm" onClick={() => setEditingVariation(null)} style={{ padding: '2px 6px' }}>X</button>
                                  </div>
                                ) : (
                                  v.cogs != null ? formatCurrency(v.cogs) : <span style={{ color: 'var(--text-light)' }}>--</span>
                                )}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                {vMargin !== null
                                  ? <span style={{ color: vMargin > 30 ? 'var(--success)' : vMargin > 15 ? 'var(--warning)' : 'var(--danger)' }}>{vMargin.toFixed(1)}%</span>
                                  : <span style={{ color: 'var(--text-light)' }}>--</span>}
                              </td>
                              <td style={{ textAlign: 'center' }}>{v.total_sold || 0}</td>
                              <td>
                                <button className="btn btn-secondary btn-sm"
                                  onClick={() => { setEditingVariation(v.id); setEditVariationCogs(v.cogs != null ? String(v.cogs) : ''); }}
                                  title="Edit COGS"
                                  style={{ padding: '4px 8px', fontSize: '14px', lineHeight: 1 }}>&#9998;</button>
                              </td>
                            </tr>
                          );
                        })}
                        {isExpanded && hasMultiple && !groupVariations[key] && (
                          <tr><td colSpan={8} style={{ textAlign: 'center', padding: '12px', color: 'var(--text-light)' }}>Loading variations...</td></tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {productGroups.length === 0 && (
                    <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-light)', padding: '24px' }}>No products found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {productTotal > 50 && (
              <div className="pagination">
                <span className="pagination-info">{productTotal} products ({productTotalSkus} SKUs total)</span>
                <div className="pagination-btns">
                  <button className="btn btn-secondary btn-sm" disabled={productPage <= 1}
                          onClick={() => { setProductPage(p => p - 1); fetchProducts(productSearch, productPage - 1); }}>Prev</button>
                  <button className="btn btn-secondary btn-sm" disabled={productGroups.length < 50}
                          onClick={() => { setProductPage(p => p + 1); fetchProducts(productSearch, productPage + 1); }}>Next</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Break Products tab */}
      {tab === 'break-products' && <BreakProductsTab />}

      {/* Import tab */}
      {tab === 'import' && (
        <div>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '16px' }}>Import Orders from CSV</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Upload a TikTok Shop order export CSV. Orders are matched by Order ID — re-importing the same file will update existing records, not create duplicates.
            </p>
            {importCount > 0 && (
              <div style={{ marginBottom: '16px', padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px', fontSize: '13px' }}>
                <strong>{importCount}</strong> orders currently imported from CSV
              </div>
            )}
            <form onSubmit={uploadImport}>
              <div className="form-row">
                <div className="form-group">
                  <label>Shop</label>
                  <select value={importShopId} onChange={e => setImportShopId(e.target.value)}>
                    {importShops.length === 0 && <option value="">No active shops</option>}
                    {importShops.map(s => (
                      <option key={s.shop_id} value={s.shop_id}>{s.shop_name} ({s.shop_id})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>CSV File</label>
                  <input type="file" accept=".csv" onChange={e => setImportFile(e.target.files[0])} />
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={importLoading || !importFile || !importShopId}>
                {importLoading ? 'Importing...' : 'Upload & Import'}
              </button>
            </form>
            {importResult && (
              <div style={{ marginTop: '16px', padding: '12px 16px', borderRadius: '8px',
                background: importResult.error ? '#fff5f5' : '#f0fff4',
                border: `1px solid ${importResult.error ? 'var(--danger)' : 'var(--success)'}` }}>
                {importResult.error ? (
                  <div style={{ color: 'var(--danger)' }}>{importResult.error}</div>
                ) : importResult.cleared ? (
                  <div style={{ color: 'var(--success)' }}>
                    Cleared {importResult.deletedOrders} orders and {importResult.deletedBuyers} orphaned buyers.
                  </div>
                ) : (
                  <div>
                    <div style={{ color: 'var(--success)', fontWeight: 600 }}>
                      Import complete: {importResult.imported} new, {importResult.updated} updated
                    </div>
                    {importResult.errors?.length > 0 && (
                      <div style={{ marginTop: '8px', color: 'var(--danger)', fontSize: '13px' }}>
                        {importResult.errors.length} errors:
                        <ul style={{ margin: '4px 0 0 16px' }}>
                          {importResult.errors.slice(0, 10).map((e, i) => (
                            <li key={i}>Order {e.orderId}: {e.error}</li>
                          ))}
                          {importResult.errors.length > 10 && <li>...and {importResult.errors.length - 10} more</li>}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="card" style={{ marginTop: '24px' }}>
            <h3 className="card-title" style={{ marginBottom: '16px' }}>Import Ad Spend & Auction COGS</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Upload a CSV with columns: <strong>Date</strong>, <strong>Ad Spend</strong>, <strong>Auction COGS</strong>. Values are added to existing data for each date (not overwritten). Dates can be MM/DD/YYYY or YYYY-MM-DD format.
            </p>
            {metricsCount > 0 && (
              <div style={{ marginBottom: '16px', padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px', fontSize: '13px' }}>
                <strong>{metricsCount}</strong> dates with imported ad spend / auction COGS
              </div>
            )}
            <form onSubmit={uploadMetrics}>
              <div className="form-row">
                <div className="form-group">
                  <label>Shop</label>
                  <select value={importShopId} onChange={e => setImportShopId(e.target.value)}>
                    {importShops.length === 0 && <option value="">No active shops</option>}
                    {importShops.map(s => (
                      <option key={s.shop_id} value={s.shop_id}>{s.shop_name} ({s.shop_id})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>CSV File</label>
                  <input type="file" accept=".csv" onChange={e => setMetricsFile(e.target.files[0])} />
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={metricsLoading || !metricsFile || !importShopId}>
                {metricsLoading ? 'Importing...' : 'Upload & Import'}
              </button>
            </form>
            {metricsResult && (
              <div style={{ marginTop: '16px', padding: '12px 16px', borderRadius: '8px',
                background: metricsResult.error ? '#fff5f5' : '#f0fff4',
                border: `1px solid ${metricsResult.error ? 'var(--danger)' : 'var(--success)'}` }}>
                {metricsResult.error ? (
                  <div style={{ color: 'var(--danger)' }}>{metricsResult.error}</div>
                ) : (
                  <div>
                    <div style={{ color: 'var(--success)', fontWeight: 600 }}>
                      Import complete: {metricsResult.created} new dates, {metricsResult.updated} updated
                    </div>
                    {metricsResult.errors?.length > 0 && (
                      <div style={{ marginTop: '8px', color: 'var(--danger)', fontSize: '13px' }}>
                        {metricsResult.errors.length} errors:
                        <ul style={{ margin: '4px 0 0 16px' }}>
                          {metricsResult.errors.slice(0, 10).map((e, i) => (
                            <li key={i}>Row {e.row}: {e.error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {(importCount > 0 || metricsCount > 0) && (
            <div style={{ marginTop: '24px' }}>
              <button className="btn btn-danger" onClick={clearImportedData}>
                Clear All Imported Data
              </button>
            </div>
          )}
        </div>
      )}

      {/* API Health tab */}
      {tab === 'api' && apiHealth && (
        <div>
          <div className="kpi-grid">
            {apiHealth.syncStatuses.map(s => (
              <div key={s.source} className="kpi-card">
                <div className="kpi-label">{s.source.replace(/_/g, ' ')}</div>
                <div style={{ marginTop: '8px' }}>
                  <div style={{ fontSize: '12px' }}>Last Success: {s.last_success_at ? formatDateTime(s.last_success_at) : 'Never'}</div>
                  {s.last_failure_at && (!s.last_success_at || s.last_failure_at > s.last_success_at) && (
                    <>
                      <div style={{ fontSize: '12px', color: 'var(--danger)' }}>Last Failure: {formatDateTime(s.last_failure_at)}</div>
                      {s.last_error && <div style={{ fontSize: '11px', color: 'var(--danger)' }}>{s.last_error}</div>}
                    </>
                  )}
                  <div style={{ marginTop: '6px' }}>
                    <span className={`badge ${s.is_syncing ? 'badge-warning' : 'badge-success'}`}>{s.is_syncing ? 'Syncing...' : 'Idle'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Token Status</h3>
            {apiHealth.shops.map(s => (
              <div key={s.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Shop: {s.shop_name} ({s.shop_id})</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className={`badge ${s.tokenValid ? 'badge-success' : 'badge-danger'}`}>
                      {s.tokenValid ? (s.tokenExpiringSoon ? 'Expiring Soon' : 'Valid') : 'Expired/Missing'}
                    </span>
                    <button className="btn btn-sm btn-danger" onClick={async () => {
                      if (!window.confirm(`Disconnect ${s.shop_name} (${s.shop_id})? This will clear tokens and backfill progress.`)) return;
                      await api.delete(`/api/admin/shops/${s.id}`);
                      fetchTabData('api');
                    }}>Disconnect</button>
                  </div>
                </div>
                {s.tokenValid && !s.shop_cipher && (
                  <div style={{ marginTop: '6px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input type="text" placeholder="Paste shop cipher from TikTok Partner Center" id={`cipher-${s.id}`}
                      style={{ flex: 1, padding: '4px 8px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '4px' }} />
                    <button className="btn btn-sm btn-primary" onClick={async () => {
                      const cipher = document.getElementById(`cipher-${s.id}`).value.trim();
                      if (!cipher) return;
                      await api.put(`/api/admin/shops/${s.id}/cipher`, { shop_cipher: cipher });
                      fetchTabData('api');
                    }}>Save Cipher</button>
                  </div>
                )}
                {s.shop_cipher && <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>Cipher: {s.shop_cipher}</div>}
              </div>
            ))}
            {apiHealth.adsAccounts.map(a => (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span>Ads: {a.account_name} ({a.account_id})</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className={`badge ${a.tokenValid ? 'badge-success' : 'badge-danger'}`}>
                    {a.tokenValid ? (a.tokenExpiringSoon ? 'Expiring Soon' : 'Valid') : 'Expired/Missing'}
                  </span>
                  <button className="btn btn-sm btn-danger" onClick={async () => {
                    if (!window.confirm(`Disconnect ${a.account_name} (${a.account_id})?`)) return;
                    await api.delete(`/api/admin/ads-accounts/${a.id}`);
                    fetchTabData('api');
                  }}>Disconnect</button>
                </div>
              </div>
            ))}
            {apiHealth.shops.length === 0 && apiHealth.adsAccounts.length === 0 && (
              <p style={{ color: 'var(--text-light)' }}>No API connections configured. Connect TikTok Shop and Ads from the buttons below.</p>
            )}
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
              <a href="/auth/tiktok-shop" className="btn btn-primary">Connect TikTok Shop</a>
              <a href="/auth/tiktok-ads" className="btn btn-primary">Connect TikTok Ads</a>
            </div>
          </div>

          {backfill.length > 0 && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '12px' }}>Backfill Progress</h3>
              {backfill.map(b => (
                <div key={b.id} style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span>{b.source}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className={`badge ${b.status === 'completed' ? 'badge-success' : b.status === 'failed' ? 'badge-danger' : b.status === 'in_progress' ? 'badge-warning' : 'badge-default'}`}>{b.status}</span>
                      <button className="btn btn-sm" style={{ fontSize: '11px' }} onClick={async () => {
                        if (!window.confirm(`Reset backfill for ${b.source}? It will re-run on the next sync cycle.`)) return;
                        await api.delete(`/api/admin/backfill-progress/${b.source}`);
                        fetchTabData('api');
                      }}>Reset</button>
                    </div>
                  </div>
                  <div style={{ background: 'var(--border)', borderRadius: '4px', height: '8px' }}>
                    <div style={{ background: 'var(--accent)', borderRadius: '4px', height: '100%', width: `${(b.completed_days / b.total_days) * 100}%`, transition: 'width 0.3s' }} />
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>{b.completed_days}/{b.total_days} days</div>
                </div>
              ))}
            </div>
          )}

          <OrderStatusRefresh />
        </div>
      )}

      {/* Backups tab */}
      {tab === 'backups' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Database Backups</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <a href="/api/admin/download-db" className="btn btn-secondary" download>Download Database</a>
              <button className="btn btn-primary" onClick={runBackup}>Run Backup Now</button>
            </div>
          </div>
          <div className="table-container">
            <table>
              <thead><tr><th>Filename</th><th>Size</th><th>Status</th><th>S3 Key</th><th>Date</th></tr></thead>
              <tbody>
                {backups.map(b => (
                  <tr key={b.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{b.filename}</td>
                    <td>{b.size_bytes ? (b.size_bytes / 1024).toFixed(1) + ' KB' : '--'}</td>
                    <td><span className={`badge ${b.status === 'success' ? 'badge-success' : 'badge-danger'}`}>{b.status}</span></td>
                    <td style={{ fontSize: '12px' }}>{b.s3_key || 'Local'}</td>
                    <td>{formatDateTime(b.created_at)}</td>
                  </tr>
                ))}
                {backups.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-light)' }}>No backups yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* History tab — revertable changes with confirm modal */}
      {tab === 'history' && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '8px' }}>Recent Changes</h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: '12px' }}>
            Recent admin changes with a one-click revert where we know how to undo them. Entries from before this tab shipped, or actions we don't yet support, show as audit-only.
          </p>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>When</th>
                  <th style={{ textAlign: 'left' }}>Who</th>
                  <th style={{ textAlign: 'left' }}>What</th>
                  <th style={{ textAlign: 'right', width: 140 }}>Revert</th>
                </tr>
              </thead>
              <tbody>
                {history.items.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-light)', padding: 24 }}>No activity yet.</td></tr>
                )}
                {history.items.map(item => (
                  <tr key={item.id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-secondary)' }}>{formatDateTime(item.created_at)}</td>
                    <td style={{ fontSize: 13 }}>{item.user_name || 'System'}</td>
                    <td style={{ fontSize: 13 }}>
                      {item.summary}
                      {item.reverted_at && (
                        <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>
                          Reverted by {item.reverted_by_user_name || 'someone'} at {formatDateTime(item.reverted_at)}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {item.reverted_at ? (
                        <span style={{ fontSize: 12, color: 'var(--text-light)' }}>Reverted</span>
                      ) : item.revertable ? (
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={historyBusy === item.id}
                          onClick={() => setHistoryConfirm(item)}
                        >
                          Revert
                        </button>
                      ) : (
                        <span
                          style={{ fontSize: 12, color: 'var(--text-light)' }}
                          title={item.revertable_known ? 'Original change predates revert support' : 'This action type has no revert handler yet'}
                        >
                          Audit only
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination" style={{ marginTop: 12 }}>
            <span className="pagination-info">Page {history.page} of {Math.max(1, Math.ceil(history.total / 50))}</span>
            <div className="pagination-btns">
              <button className="btn btn-secondary btn-sm" disabled={history.page <= 1}
                      onClick={() => { setHistory(h => ({ ...h, page: h.page - 1 })); fetchTabData(); }}>Prev</button>
              <button className="btn btn-secondary btn-sm" disabled={history.items.length < 50}
                      onClick={() => { setHistory(h => ({ ...h, page: h.page + 1 })); fetchTabData(); }}>Next</button>
            </div>
          </div>

          {historyConfirm && (
            <div className="modal-overlay" onClick={() => setHistoryConfirm(null)}>
              <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
                <h3 style={{ marginTop: 0 }}>Revert this change?</h3>
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {historyConfirm.summary}
                </p>
                <div style={{ padding: 8, background: '#f1f5f9', borderRadius: 6, fontSize: 12, fontFamily: 'ui-monospace, monospace', whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto' }}>
                  {JSON.stringify(historyConfirm.details, null, 2)}
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 8 }}>
                  This restores the captured before-state. If something else has changed in the same area since, that may conflict.
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setHistoryConfirm(null)} disabled={historyBusy === historyConfirm.id}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={historyBusy === historyConfirm.id}
                    onClick={async () => {
                      setHistoryBusy(historyConfirm.id);
                      try {
                        await api.post(`/api/admin/history/${historyConfirm.id}/revert`);
                        const h = await api.get(`/api/admin/history?page=${history.page}`);
                        setHistory(h.data);
                        setHistoryConfirm(null);
                      } catch (err) {
                        alert(err?.response?.data?.error || 'Revert failed');
                      } finally {
                        setHistoryBusy(null);
                      }
                    }}
                  >
                    {historyBusy === historyConfirm.id ? 'Reverting…' : 'Revert'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Activity Log tab */}
      {tab === 'activity' && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '16px' }}>Activity Log</h3>
          {activityLog.logs.map(l => (
            <div key={l.id} className="activity-item">
              <span className="activity-time">{formatDateTime(l.created_at)}</span>
              <span><strong>{l.user_name || 'System'}</strong> {l.action} {l.entity_type ? `on ${l.entity_type} #${l.entity_id}` : ''}</span>
            </div>
          ))}
          <div className="pagination">
            <span className="pagination-info">Page {activityLog.page} of {Math.ceil(activityLog.total / 50)}</span>
            <div className="pagination-btns">
              <button className="btn btn-secondary btn-sm" disabled={activityLog.page <= 1}
                      onClick={() => { setActivityLog(a => ({ ...a, page: a.page - 1 })); fetchTabData(); }}>Prev</button>
              <button className="btn btn-secondary btn-sm" disabled={activityLog.logs.length < 50}
                      onClick={() => { setActivityLog(a => ({ ...a, page: a.page + 1 })); fetchTabData(); }}>Next</button>
            </div>
          </div>
        </div>
      )}

      {/* Username Matcher tab */}
      {tab === 'usernames' && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '8px' }}>TikTok Username Matcher</h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
            Walks every break from <strong>#36 onward</strong> in <strong>pending</strong> or <strong>completed</strong> and
            matches each spot's typed username to the TikTok order whose product/SKU name carries
            the same <code>#NNN TEAM / TEAM</code> marker, after reverse-replaying trades to find
            the original buyer. (Breaks before #36 didn't embed the marker, so there's nothing to
            match against.) Writes <code>buyers.username</code> only when it's still NULL. Never
            touches <code>customer_name</code>. Proposed mappings are deduped by buyer_id and
            sorted by lifetime spend.
          </p>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" disabled={usernameLoading} onClick={() => runUsernameMatcher(true)}>
              {usernameLoading && usernameRanMode !== 'wet' ? 'Running…' : 'Dry Run (preview)'}
            </button>
            <button className="btn btn-primary" disabled={usernameLoading || !usernameResult || (usernameResult.proposals?.length || 0) === 0} onClick={() => {
              const count = usernameResult.proposals?.length || 0;
              if (window.confirm(`Apply ${count} username write${count === 1 ? '' : 's'} to the buyers table? Manual edits are preserved by the username IS NULL guard.`)) {
                runUsernameMatcher(false);
              }
            }}>
              {usernameLoading && usernameRanMode === 'wet' ? 'Applying…' : `Apply All${usernameResult ? ` (${usernameResult.proposals?.length || 0})` : ''}`}
            </button>
          </div>

          {usernameError && (
            <div style={{ padding: '12px 16px', background: '#fff5f5', border: '1px solid var(--danger)', borderRadius: 6, color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>
              {usernameError}
            </div>
          )}

          {usernameResult && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
                <div className="card" style={{ padding: 12, margin: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Breaks scanned</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{usernameResult.breaks_scanned}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-light)' }}>pending or completed</div>
                </div>
                <div className="card" style={{ padding: 12, margin: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Spots considered</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{usernameResult.spots_considered}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-light)' }}>{usernameResult.signals_emitted} signals emitted</div>
                </div>
                <div className="card" style={{ padding: 12, margin: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Writes</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: usernameResult.writes > 0 ? 'var(--success)' : 'var(--text-light)' }}>{usernameResult.writes}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-light)' }}>{usernameRanMode === 'dry' ? 'dry run — no DB changes' : 'committed to buyers.username'}</div>
                </div>
                <div className="card" style={{ padding: 12, margin: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Conflicts</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: usernameResult.conflicts.length > 0 ? 'var(--danger)' : 'var(--text-light)' }}>{usernameResult.conflicts.length}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-light)' }}>multi-username / handle-collision</div>
                </div>
              </div>

              <div style={{ marginBottom: 16, fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span>Skipped — no typed username: <strong>{usernameResult.skipped.no_username}</strong></span>
                <span>no matching order: <strong>{usernameResult.skipped.no_matching_order}</strong></span>
                <span>ambiguous match: <strong>{usernameResult.skipped.ambiguous_match}</strong></span>
                <span>empty owner: <strong>{usernameResult.skipped.empty_owner}</strong></span>
              </div>

              {usernameResult.diagnostics?.breaks_without_marker_orders > 0 && (
                <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                  <strong>{usernameResult.diagnostics.breaks_without_marker_orders}</strong> break{usernameResult.diagnostics.breaks_without_marker_orders === 1 ? '' : 's'} skipped — no order carried the <code>#NNN</code> marker in its product/SKU name (older breaks that pre-date the convention).
                </div>
              )}

              {usernameResult.diagnostics?.breaks_with_unparsed_candidates > 0 && (
                <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#92400e' }}>
                    {usernameResult.diagnostics.breaks_with_unparsed_candidates} break{usernameResult.diagnostics.breaks_with_unparsed_candidates === 1 ? '' : 's'} had matching orders but the team format didn't align
                  </div>
                  <div style={{ fontSize: 12, color: '#78350f', marginBottom: 6 }}>
                    We found <code>#NNN</code> orders for these breaks but the team-token format in the product name didn't match the break's spots. Check the sample text — usually a team-name typo on TikTok or a spot name in Nucleus that doesn't match the listing.
                  </div>
                  {usernameResult.diagnostics.sample_failures.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>Sample (up to 5):</div>
                      <table style={{ width: '100%', fontSize: 12, color: '#78350f' }}>
                        <thead>
                          <tr style={{ textAlign: 'left' }}>
                            <th style={{ padding: '4px 8px' }}>Break</th>
                            <th style={{ padding: '4px 8px', textAlign: 'right' }}>Orders found</th>
                            <th style={{ padding: '4px 8px' }}>Sample unparsed text</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usernameResult.diagnostics.sample_failures.map((f, i) => (
                            <tr key={i}>
                              <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>#{f.breakNumber}</td>
                              <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{f.candidate_line_items}</td>
                              <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 11 }}>{(f.sample_unparsed || []).join(' | ') || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {usernameResult.proposals && usernameResult.proposals.length > 0 ? (
                <>
                  <h4 style={{ marginBottom: 8 }}>
                    {usernameRanMode === 'dry' ? 'Proposed mappings' : 'Mappings written'}
                  </h4>
                  <div className="table-container" style={{ maxHeight: '60vh', overflow: 'auto' }}>
                    {(() => {
                      const cols = [
                        { key: 'buyer_id',              label: 'Buyer ID',                  type: 'string', align: 'left'  },
                        { key: 'current_customer_name', label: 'Customer Name (shipping)',  type: 'string', align: 'left'  },
                        { key: 'username',              label: usernameRanMode === 'dry' ? 'Proposed Username' : 'Username', type: 'string', align: 'left' },
                        { key: 'lifetime_value',        label: 'Lifetime Spend',            type: 'number', align: 'right' },
                        { key: 'signal_count',          label: 'Signals',                   type: 'number', align: 'right' },
                        { key: 'alternate_count',       label: 'Alts',                      type: 'number', align: 'right' },
                      ];
                      const toggleSort = (col) => {
                        if (usernameSortKey === col.key) {
                          setUsernameSortDir(d => d === 'asc' ? 'desc' : 'asc');
                        } else {
                          setUsernameSortKey(col.key);
                          setUsernameSortDir(col.type === 'string' ? 'asc' : 'desc');
                        }
                      };
                      const activeCol = cols.find(c => c.key === usernameSortKey) || cols[0];
                      const dir = usernameSortDir === 'asc' ? 1 : -1;
                      const sorted = [...usernameResult.proposals].sort((a, b) => {
                        const av = a[activeCol.key];
                        const bv = b[activeCol.key];
                        if (activeCol.type === 'string') {
                          return (av || '').localeCompare(bv || '', undefined, { sensitivity: 'base' }) * dir;
                        }
                        if (av == null && bv == null) return 0;
                        if (av == null) return 1;
                        if (bv == null) return -1;
                        return ((av ?? 0) - (bv ?? 0)) * dir;
                      });
                      return (
                        <table>
                          <thead>
                            <tr>
                              {cols.map(col => (
                                <th key={col.key}
                                    onClick={() => toggleSort(col)}
                                    style={{ textAlign: col.align, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                                  {col.label}{' '}
                                  {usernameSortKey === col.key ? (usernameSortDir === 'asc' ? '↑' : '↓') : ''}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sorted.map(p => (
                              <tr key={`${p.shop_id}-${p.buyer_id}`}>
                                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.buyer_id}</td>
                                <td>{p.current_customer_name || <span style={{ color: 'var(--text-light)' }}>--</span>}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: 12, color: p.alternate_count > 0 ? 'var(--warning, #b45309)' : 'var(--text)' }}>
                                  {p.username}
                                </td>
                                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                  {p.lifetime_value != null ? `$${Number(p.lifetime_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '--'}
                                </td>
                                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.signal_count}</td>
                                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: p.alternate_count > 0 ? 'var(--warning, #b45309)' : 'var(--text-light)' }}>
                                  {p.alternate_count}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                </>
              ) : (
                <div style={{ padding: 16, color: 'var(--text-light)', fontSize: 13, textAlign: 'center' }}>
                  No mappings produced. Check the skipped counts above and the conflicts section for hints.
                </div>
              )}

              {usernameResult.conflicts.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h4 style={{ marginBottom: 8 }}>Conflicts ({usernameResult.conflicts.length})</h4>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                    Apply All writes the auto-picked username for each row. If a pick looks wrong, search the Buyer ID on the Customer Data page and use the inline edit on the detail modal — manual overrides aren't overwritten by future runs.
                  </div>
                  <div className="table-container" style={{ maxHeight: '40vh', overflow: 'auto' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Issue</th>
                          <th>Buyer / Username</th>
                          <th>What happened</th>
                          <th>Picked</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usernameResult.conflicts.map((c, i) => {
                          const fmt = (t) => t ? String(t).replace('T', ' ').slice(0, 16) : '—';
                          if (c.type === 'multi_username_per_buyer') {
                            const chosen = c.chosen || {};
                            const others = (c.alternates || []).map(a => `${a.display} (used ${a.count}× — last ${fmt(a.lastPaidTime)})`);
                            return (
                              <tr key={i}>
                                <td><span className="badge badge-warning">Multiple usernames for one buyer</span></td>
                                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.buyerId}</td>
                                <td style={{ fontSize: 13 }}>
                                  This buyer was typed as <strong>{1 + (c.alternates?.length || 0)}</strong> different usernames across their spots. The operator may have typo'd, the buyer changed their handle, or the same person uses different names per break.
                                  <div style={{ marginTop: 4, color: 'var(--text-secondary)', fontSize: 12 }}>
                                    Other usernames seen: {others.join(' · ') || '—'}
                                  </div>
                                </td>
                                <td style={{ fontSize: 13 }}>
                                  <div style={{ fontFamily: 'monospace' }}>{chosen.username}</div>
                                  <div style={{ fontSize: 11, color: 'var(--text-light)' }}>
                                    used {chosen.count}× · last {fmt(chosen.lastPaidTime)} · {chosen.reason || 'most recent'}
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                          if (c.type === 'username_collision') {
                            const buyers = (c.buyers || []).map(b => b.buyerId);
                            return (
                              <tr key={i}>
                                <td><span className="badge badge-danger">Same username, multiple buyers</span></td>
                                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.username}</td>
                                <td style={{ fontSize: 13 }}>
                                  This username is the proposed match for <strong>{buyers.length}</strong> different TikTok buyer_ids. Likely the same person with multiple TikTok accounts (alts), or a typo collision.
                                  <div style={{ marginTop: 4, color: 'var(--text-secondary)', fontSize: 12 }}>
                                    Buyer IDs: <span style={{ fontFamily: 'monospace' }}>{buyers.join(', ')}</span>
                                  </div>
                                </td>
                                <td style={{ fontSize: 13, color: 'var(--text-light)' }}>
                                  Each buyer_id gets <code>{c.username}</code> written separately. No merge.
                                </td>
                              </tr>
                            );
                          }
                          return (
                            <tr key={i}>
                              <td><span className="badge">{c.type}</span></td>
                              <td colSpan={3} style={{ fontFamily: 'monospace', fontSize: 12 }}>{JSON.stringify(c)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* External Apps tab */}
      {tab === 'apps' && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '16px' }}>External App Integrations</h3>
          {externalApps.map(app => (
            <div key={app.id} style={{ padding: '16px', border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '12px' }}>
              <h4>{app.app_name}</h4>
              <div className="form-group" style={{ marginTop: '12px' }}>
                <label>URL</label>
                <input defaultValue={app.app_url || ''} onBlur={e => updateExternalApp(app.id, { app_url: e.target.value })} placeholder="https://..." />
              </div>
              <div className="form-group">
                <label>SSO Secret</label>
                <input type="password" defaultValue={app.sso_secret || ''} onBlur={e => updateExternalApp(app.id, { sso_secret: e.target.value })} placeholder="Shared secret for SSO" />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={!!app.enabled} onChange={e => updateExternalApp(app.id, { enabled: e.target.checked ? 1 : 0 })} style={{ width: 'auto' }} />
                Show in navigation
              </label>
            </div>
          ))}
        </div>
      )}

      {/* Surprise Set Template tab */}
      {tab === 'surprise' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <h3 className="card-title" style={{ marginBottom: '8px' }}>Surprise Set Template</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px', maxWidth: '640px' }}>
                Define the columns of the "Surprise Set Batch" spreadsheet generated for a break — add, reorder, rename,
                enable/disable columns and set the value written into every row. Keep the column order and header text
                matching TikTok's current template. The "Team markets" column is filled per row with the team names.
              </p>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={downloadSurpriseSample}>Download sample</button>
          </div>

          {surpriseCols.filter(c => c.enabled && c.fill?.mode === 'teamMarkets').length !== 1 && (
            <p style={{ color: 'var(--warning-text, #b7791f)', fontSize: '12px', marginBottom: '8px' }}>
              ⚠ Exactly one enabled column should use the "Team markets" fill (the product-name column).
            </p>
          )}

          {(() => {
            // Transposed, spreadsheet-style grid: each config column is a spreadsheet
            // column running left→right; rows mirror the generated file (Header = row 1,
            // Mandatory = row 2, Instruction = row 3, Example = row 4), then the fill
            // config that produces data rows 5+.
            const bd = '1px solid var(--border)';
            const labelCell = { position: 'sticky', left: 0, zIndex: 2, background: 'var(--bg-card)', border: bd, borderRight: '2px solid var(--border)', padding: '6px 10px', fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap', color: 'var(--text-secondary)', textAlign: 'left' };
            const cornerCell = { ...labelCell, zIndex: 3, background: 'var(--bg)' };
            const colHead = { border: bd, padding: '4px 6px', background: 'var(--bg)', minWidth: '210px' };
            const dataCell = { border: bd, padding: '3px 4px', verticalAlign: 'top', minWidth: '210px' };
            const cellInput = { width: '100%', boxSizing: 'border-box', border: 'none', background: 'transparent', font: 'inherit', padding: '4px 6px' };
            const rowNum = { color: 'var(--text-light)', fontWeight: 400, marginLeft: '6px' };
            const dim = (col) => ({ opacity: col.enabled ? 1 : 0.45 });
            const cols = surpriseCols;
            const attrRow = (label, num, render) => (
              <tr>
                <td style={labelCell}>{label}{num != null && <span style={rowNum}>{num}</span>}</td>
                {cols.map((col, i) => (
                  <td key={col.id} style={{ ...dataCell, ...dim(col) }}>{render(col, i)}</td>
                ))}
                <td style={dataCell} />
              </tr>
            );
            return (
              <div className="table-container" style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
                  <thead>
                    <tr>
                      <th style={cornerCell}>Column</th>
                      {cols.map((col, i) => (
                        <th key={col.id} style={{ ...colHead, opacity: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                            <button className="ss-iconbtn" disabled={i === 0} onClick={() => moveCol(i, -1)} title="Move left" aria-label="Move column left">‹</button>
                            <label className="ss-toggle" title={col.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}>
                              <input type="checkbox" checked={!!col.enabled} onChange={e => updateCol(i, { enabled: e.target.checked })} />
                              <span className="ss-slider" />
                            </label>
                            <button className="ss-iconbtn" disabled={i === cols.length - 1} onClick={() => moveCol(i, 1)} title="Move right" aria-label="Move column right">›</button>
                            <button className="ss-delbtn" onClick={() => deleteCol(i)} title="Delete column" aria-label="Delete column">✕</button>
                          </div>
                        </th>
                      ))}
                      <th style={{ ...colHead, minWidth: '80px', textAlign: 'center' }}>
                        <button className="btn btn-secondary btn-sm" onClick={addCol}>+ Add</button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {attrRow('Header', 1, (col, i) => (
                      <input type="text" value={col.header} placeholder="Header"
                        onChange={e => updateCol(i, { header: e.target.value })} style={{ ...cellInput, fontWeight: 600 }} />
                    ))}
                    {attrRow('Mandatory', 2, (col, i) => (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)', padding: '4px 6px' }}>
                        <input type="checkbox" checked={!!col.required} onChange={e => updateCol(i, { required: e.target.checked })} style={{ width: 'auto' }} />
                        {col.required ? 'Mandatory' : 'Optional'}
                      </label>
                    ))}
                    {attrRow('Instruction', 3, (col, i) => (
                      <textarea value={col.instruction} rows={3} placeholder="—"
                        onChange={e => updateCol(i, { instruction: e.target.value })} style={{ ...cellInput, resize: 'vertical' }} />
                    ))}
                    {attrRow('Example', 4, (col, i) => (
                      <input type="text" value={col.example} placeholder="—"
                        onChange={e => updateCol(i, { example: e.target.value })} style={{ ...cellInput, fontStyle: 'italic', color: 'var(--text-secondary)' }} />
                    ))}
                    <tr><td colSpan={cols.length + 2} style={{ border: bd, background: 'var(--bg)', padding: '4px 10px', fontSize: '11px', fontWeight: 600, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>How each data row is filled</td></tr>
                    {attrRow('Fill', null, (col, i) => (
                      <select value={col.fill?.mode || 'blank'} onChange={e => updateCol(i, { fill: { mode: e.target.value } })} style={cellInput}>
                        <option value="teamMarkets">Team markets</option>
                        <option value="default">Default value</option>
                        <option value="blank">Blank</option>
                      </select>
                    ))}
                    {attrRow('Data value', '5+', (col, i) => (
                      <input type="text"
                        value={col.fill?.mode === 'default' ? (col.fill?.value || '') : ''}
                        disabled={col.fill?.mode !== 'default'}
                        placeholder={col.fill?.mode === 'teamMarkets' ? '(team names)' : col.fill?.mode === 'blank' ? '(blank)' : 'value'}
                        onChange={e => updateCol(i, { fill: { value: e.target.value } })} style={cellInput} />
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
            <button className="btn btn-secondary btn-sm" onClick={addCol}>+ Add Column</button>
            <button className="btn btn-primary" onClick={saveSurpriseCols} disabled={!surpriseDirty || surpriseSaving}>
              {surpriseSaving ? 'Saving…' : 'Save'}
            </button>
            {surpriseDirty && !surpriseSaving && <span style={{ color: 'var(--text-light)', fontSize: '12px' }}>Unsaved changes</span>}
            {surpriseSaved && !surpriseDirty && <span style={{ color: 'var(--success)', fontSize: '13px', fontWeight: 600 }}>✓ Saved</span>}
            {surpriseError && <span style={{ color: 'var(--danger)', fontSize: '13px' }}>{surpriseError}</span>}
          </div>

          {(() => {
            // Live preview of the generated download for a Multi-Sport break #8819.
            // Reflects the current (even unsaved) editor state; team names come from the server.
            const enabled = surpriseCols.filter(c => c.enabled);
            if (!enabled.length || !surprisePreviewLines.length) return null;
            const bd = '1px solid var(--border)';
            const th = { border: bd, padding: '6px 10px', background: '#1a202c', color: '#fff', fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'left', position: 'sticky', top: 22 };
            const letterTh = { border: bd, padding: '2px 8px', height: 22, lineHeight: '18px', background: 'var(--bg)', color: 'var(--text-secondary)', fontWeight: 600, fontSize: '11px', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 };
            const td = { border: bd, padding: '5px 10px', fontSize: '12px', whiteSpace: 'nowrap' };
            const colLetter = (idx) => { let n = idx + 1, s = ''; while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); } return s; };
            const cellFor = (col, line) => {
              const mode = col.fill?.mode;
              if (mode === 'teamMarkets') return line;
              if (mode === 'default') return col.fill?.value ?? '';
              return '';
            };
            return (
              <div style={{ marginTop: '28px' }}>
                <h4 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600 }}>Download preview</h4>
                <p style={{ color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '10px' }}>
                  What the generated "Surprise Set Batch" spreadsheet looks like for a Multi-Sport break #8819 ({surprisePreviewLines.length} rows). Updates live as you edit above.
                </p>
                <div className="table-container" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '360px', border: bd, borderRadius: '6px' }}>
                  <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th style={letterTh} />
                        {enabled.map((col, idx) => <th key={col.id} style={letterTh}>{colLetter(idx)}</th>)}
                      </tr>
                      <tr>
                        <th style={{ ...th, color: 'rgba(255,255,255,0.6)' }}>#</th>
                        {enabled.map(col => <th key={col.id} style={th}>{col.header || '—'}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {surprisePreviewLines.map((line, r) => (
                        <tr key={r}>
                          <td style={{ ...td, color: 'var(--text-light)' }}>{r + 1}</td>
                          {enabled.map(col => <td key={col.id} style={td}>{cellFor(col, line)}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
